/**
 * Environment configuration utility
 * Centralizes environment variable access and validation
 */

interface EnvironmentConfig {
  supabase: {
    url: string;
    anonKey: string;
  };
  apis: {
    tradier: string;
    finnhub: string;
    perplexity: string;
    fred?: string;
    newsio?: string;
  };
  isDevelopment: boolean;
  isProduction: boolean;
}

class Environment {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.loadConfig();
    this.validateConfig();
  }

  private loadConfig(): EnvironmentConfig {
    return {
      supabase: {
        url: import.meta.env.VITE_SUPABASE_URL || '',
        anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
      },
      apis: {
        tradier: import.meta.env.VITE_TRADIER_API_KEY || '',
        finnhub: import.meta.env.VITE_FINNHUB_API_KEY || '',
        perplexity: import.meta.env.VITE_PERPLEXITY_API_KEY || '',
        fred: import.meta.env.VITE_FRED_API_KEY,
        newsio: import.meta.env.VITE_NEWSIO_API_KEY,
      },
      isDevelopment: import.meta.env.DEV,
      isProduction: import.meta.env.PROD,
    };
  }

  private validateConfig(): void {
    // Only validate in production or when explicitly required
    if (!this.config.isDevelopment) {
      const requiredFields = [
        { key: 'VITE_SUPABASE_URL', value: this.config.supabase.url },
        { key: 'VITE_SUPABASE_ANON_KEY', value: this.config.supabase.anonKey },
        { key: 'VITE_TRADIER_API_KEY', value: this.config.apis.tradier },
        { key: 'VITE_FINNHUB_API_KEY', value: this.config.apis.finnhub },
        { key: 'VITE_PERPLEXITY_API_KEY', value: this.config.apis.perplexity },
      ];

      const missingFields = requiredFields.filter(
        field => !field.value || field.value.includes('your_') || field.value.includes('_here')
      );

      if (missingFields.length > 0) {
        const missing = missingFields.map(f => f.key).join(', ');
        throw new Error(
          `Missing or invalid environment variables: ${missing}. Please check your .env file.`
        );
      }
    }
  }

  get supabase() {
    return this.config.supabase;
  }

  get apis() {
    return this.config.apis;
  }

  get isDevelopment() {
    return this.config.isDevelopment;
  }

  get isProduction() {
    return this.config.isProduction;
  }

  hasApiKey(service: keyof EnvironmentConfig['apis']): boolean {
    const key = this.config.apis[service];
    return Boolean(key && !key.includes('your_') && !key.includes('_here'));
  }
}

export const env = new Environment();